import React, { useContext, useState, useEffect, useMemo } from 'react';
import ShippingForm from '../components/ShippingForm';
import PaymentMethod from '../components/PaymentMethod';
import { ShopContext } from '../context/shopcontext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FaQuestionCircle } from 'react-icons/fa';
import { ChevronRight, Gift } from 'lucide-react';

const PlaceOrder = () => {
  const [method, setMethod] = useState('razorpay'); // Default to Razorpay
  const { navigate, backendUrl, token, cartItems, setCartItems } = useContext(ShopContext);
  const [isGuest, setIsGuest] = useState(!token);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState('Standard');
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [saveAddress, setSaveAddress] = useState(true);
  const [cartProducts, setCartProducts] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [canCalculateDelivery, setCanCalculateDelivery] = useState(false);

  const northPrefixes = [
    // North
    '11', // Delhi
    '12',
    '13', // Haryana
    '14',
    '15', // Punjab
    '16',
    '17', // Chandigarh, HP
    '18',
    '19', // J&K, Ladakh
    '20',
    '21',
    '22',
    '23',
    '24',
    '25',
    '26',
    '27',
    '28', // UP & Uttarakhand
    '80',
    '81',
    '82',
    '83',
    '84',
    '85', // Bihar, Jharkhand

    // North-East
    '78',
    '79', // Assam, NE region

    // North-West
    '30',
    '31',
    '32',
    '33',
    '34', // Rajasthan

    // Madhya Pradesh
    '45',
    '46',
    '47',
    '48',

    // Mumbai
    '400',
    '401',
  ];
  const [showCoupons, setShowCoupons] = useState(false);

  const [availableCoupons, setAvailableCoupons] = useState([]);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await axios.get(`${backendUrl}/api/coupon/list`);

        if (res.data.success && res.data.coupons?.length > 0) {
          const activeCoupons = res.data.coupons.filter((coupon) => coupon.isActive);

          setAvailableCoupons(activeCoupons);
        } else {
          setAvailableCoupons([]);
        }
      } catch (err) {
        setAvailableCoupons([]);
      }
    };

    fetchCoupons();
  }, [backendUrl]);

  const [showShippingPolicy, setShowShippingPolicy] = useState(false);

  const initialAddress = JSON.parse(localStorage.getItem('savedAddress')) || {
    firstName: '',
    lastName: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    country: '',
    phone: '',
    otherCountry: '',
  };

  const [formData, setFormData] = useState(initialAddress);

  useEffect(() => {
    if (saveAddress) {
      localStorage.setItem('savedAddress', JSON.stringify(formData));
    }
  }, [formData, saveAddress]);

  const calculateDeliveryFee = (subtotal) => {
    if (subtotal >= 500) return 0;
    return 100;
  };

  const isHighShippingPincode = (pincode) => {
    if (!pincode) return false;
    const prefix2 = pincode.slice(0, 2);
    const prefix3 = pincode.slice(0, 3);

    return northPrefixes.includes(prefix2) || northPrefixes.includes(prefix3);
  };
  const applyCoupon = async () => {
    if (discountApplied) {
      toast.warning('Coupon already applied!');
      return;
    }

    if (!formData.email) {
      toast.error('Please enter your details before applying a coupon.');
      return;
    }

    try {
      const response = await axios.post(`${backendUrl}/api/coupon/validate`, {
        email: formData.email,
        phone: formData.phone,
        code: couponCode,
        cartTotal: subtotal,
        cartItems: Object.entries(cartItems).flatMap(([productId, sizes]) =>
          Object.entries(sizes).map(([size, quantity]) => ({
            productId,
            size,
            quantity,
            price: cartProducts.find((p) => p._id === productId)?.price || 0,
          }))
        ),
      });

      if (!response.data.success) {
        toast.error(response.data.message);
        return;
      }

      setDiscount(response.data.discount);
      setDiscountAmount(response.data.discountAmount); // ✅ set actual value
      setDiscountApplied(true);

      toast.success('Coupon applied successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to apply coupon.');
    }
  };

  useEffect(() => {
    const zip = formData.zipcode?.toString();
    if (zip && zip.length >= 4) {
      setCanCalculateDelivery(true);
    } else {
      setCanCalculateDelivery(false);
    }
  }, [formData.zipcode]);

  const initPay = (order, backendUrl, setCartItems, navigate) => {
    if (!window.Razorpay) {
      toast.error('Razorpay SDK failed to load. Please refresh the page.');
      return;
    }

    const options = {
      key: import.meta.env.VITE_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: 'Zarviya - Order Payment',
      description: 'Complete your order payment',
      order_id: order.razorpayOrderId,
      prefill: {
        email: order?.address?.email || '',
        contact: order?.address?.phone || '',
        name: `${order?.address?.firstName || ''} ${order?.address?.lastName || ''}`.trim(),
      },
      handler: async (response) => {
        toast.info('Processing payment...');

        const pollPaymentStatus = async () => {
          try {
            const statusResponse = await axios.get(
              `${backendUrl}/api/order/status/${order.razorpayOrderId}`
            );

            if (statusResponse.data.success && statusResponse.data.payment) {
              clearInterval(pollInterval);
              toast.success('Payment confirmed! 🎉');
              setCartItems({});
              navigate(`/order-details/${order.id}`);
            }
          } catch (error) {
            toast.error('Error verifying payment status.');
            setIsSubmitting(false);
            clearInterval(pollInterval);
          }
        };

        const pollInterval = setInterval(pollPaymentStatus, 5000);
      },
      modal: {
        ondismiss: () => {
          toast.warning('Payment process was interrupted. Please try again.');
          setIsSubmitting(false);
        },
      },
    };

    // ✅ Log the options passed to Razorpay
    console.log('Razorpay Options:', options);

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  useEffect(() => {
    const fetchCartProducts = async () => {
      const productIdsInCart = Object.keys(cartItems);
      if (productIdsInCart.length === 0) return;

      try {
        const response = await axios.post(`${backendUrl}/api/product/list-by-ids`, {
          productIds: productIdsInCart,
        });

        if (response.data.success) {
          setCartProducts(response.data.products);
        } else {
          toast.error('Failed to load cart products.');
        }
      } catch (error) {
        console.error('Error loading cart products:', error);
        toast.error('Error loading cart products.');
      }
    };

    fetchCartProducts();
  }, [cartItems]);

  const onChangeHandler = (event) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const onSubmitHandler = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.street ||
      !formData.city ||
      !formData.state ||
      !formData.zipcode ||
      !formData.country ||
      !formData.phone
    ) {
      toast.error('Please fill out all required fields.');
      return;
    }

    try {
      // ✅ Step 1: Fetch cart products fresh from backend
      const productIdsInCart = Object.keys(cartItems);

      const response = await axios.post(`${backendUrl}/api/product/list-by-ids`, {
        productIds: productIdsInCart,
      });

      if (!response.data.success) {
        toast.error('Failed to fetch product details. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const cartProducts = response.data.products;

      // ✅ Step 2: Prepare order items
      let orderItems = [];

      for (const productId in cartItems) {
        for (const size in cartItems[productId]) {
          const quantity = cartItems[productId][size];

          if (quantity > 0) {
            const itemInfo = structuredClone(cartProducts.find((p) => p._id === productId));

            if (!itemInfo) {
              toast.error(`Product ${productId} details not found. Please refresh and try again.`);
              setIsSubmitting(false);
              return;
            }

            itemInfo.size = size;
            itemInfo.quantity = quantity;

            orderItems.push(itemInfo);
          }
        }
      }

      const totalAmount = calculateSubtotal();
      const adjustedSubtotal = totalAmount - discountAmount;
      const deliveryFee = calculateDeliveryFee(adjustedSubtotal); // ✅ fixed

      const orderData = {
        address: formData,
        email: formData.email,
        items: orderItems,
        amount: adjustedSubtotal + deliveryFee,
        deliveryOption,
        deliveryFee,
        ...(discountApplied && { couponCode }),
      };

      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (method === 'razorpay') {
        const orderResponse = await axios.post(
          backendUrl + (isGuest ? '/api/order/razorpayguest' : '/api/order/razorpay'),
          orderData,
          { headers }
        );

        if (orderResponse.data.success) {
          initPay(orderResponse.data.order, backendUrl, setCartItems, navigate);
        } else {
          toast.error('An error occurred while processing order');
          setIsSubmitting(false);
        }
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while placing the order');
      setIsSubmitting(false);
    }
  };

  const calculateSubtotal = () => {
    let total = 0;
    for (const productId in cartItems) {
      const product = cartProducts.find((p) => p._id === productId);
      if (!product) continue;

      for (const size in cartItems[productId]) {
        const quantity = cartItems[productId][size];
        if (quantity > 0) {
          total += product.price * quantity;
        }
      }
    }
    return total;
  };

  const subtotal = useMemo(() => calculateSubtotal(), [cartItems, cartProducts]);
  const adjustedSubtotal = useMemo(() => subtotal - discountAmount, [subtotal, discountAmount]);
  const deliveryFee = useMemo(() => calculateDeliveryFee(adjustedSubtotal), [adjustedSubtotal]);
  const finalAmount = useMemo(
    () => adjustedSubtotal + deliveryFee,
    [adjustedSubtotal, deliveryFee]
  );

  // Section Title Component for consistency
  const SectionTitle = ({ text1, text2, onInfoClick }) => (
    <div className="flex flex-col items-start mb-6">
      <div className="inline-flex items-center gap-2">
        <h2 className="text-lg sm:text-2xl md:text-3xl font-semibold text-[#4c0f2e] font-poppins">
          {text1} <span className="text-[#c06a6f]">{text2}</span>
        </h2>
        {onInfoClick && (
          <FaQuestionCircle
            className="text-[#4c0f2e] text-base hover:text-[#D3756B]"
            onClick={onInfoClick}
          />
        )}
      </div>
    </div>
  );

  return (
    <form
      onSubmit={onSubmitHandler}
      className="flex flex-col sm:flex-row justify-between gap-6 pt-32  min-h-[80vh] border-t px-4 max-w-6xl mx-auto "
    >
      {/* Left Side - Delivery Address */}
      <div className="flex flex-col w-full sm:w-1/2 sm:max-w-[480px]">
        {/* Title */}
        <div className="mb-0">
          <SectionTitle text1="SHIPPING" text2="ADDRESS" />
        </div>

        {/* Input Fields */}
        <ShippingForm
          formData={formData}
          setFormData={setFormData}
          onChangeHandler={onChangeHandler}
        />
        <label className="flex items-center  gap-2 mt-3 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={saveAddress}
            onChange={(e) => setSaveAddress(e.target.checked)}
            className="accent-[#4c0f2e]"
          />
          Save this address for next time
        </label>

        {/* Shipping Methods Section */}
        <div className="mt-6">
          <SectionTitle
            text1="SHIPPING"
            text2="METHODS"
            onInfoClick={() => setShowShippingPolicy(true)}
          />

          <div className="flex flex-col gap-2 mt-2">
            <label className="flex items-center gap-2 text-gray-600 text-sm">
              <input
                type="radio"
                name="delivery"
                value="Standard"
                checked={deliveryOption === 'Standard'}
                onChange={() => setDeliveryOption('Standard')}
                className="accent-[#4c0f2e]"
              />
              Standard (3-7 Working Days)
            </label>
          </div>
        </div>

        {/* Coupon Section */}
        <div className="mt-6">
          {/* COUPON TITLE WITH TOGGLE ARROW */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg sm:text-2xl md:text-3xl font-semibold text-[#4c0f2e] font-poppins">
                APPLY <span className="text-[#c06a6f]">COUPON</span>
              </h2>
              <button
                type="button"
                onClick={() => setShowCoupons(!showCoupons)}
                className="text-[#4c0f2e] hover:text-[#D3756B] transition"
                title="View available coupons"
              >
                <ChevronRight
                  className={`w-5 h-5 sm:w-8 sm:h-8 transition-transform ${
                    showCoupons ? 'rotate-90' : ''
                  }`}
                />
              </button>
            </div>
          </div>

          {/* COUPON INPUT & APPLY */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-white to-gray-50 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row gap-3 items-stretch">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="w-full border-2 border-[#2a081a]/20 rounded-lg px-4 py-3 text-sm bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#4c0f2e] focus:border-[#2a081a] transition-all duration-200 placeholder-gray-400"
                />
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <i className="fas fa-tag text-[#2a081a]/40 text-sm"></i>
                </div>
              </div>
              <button
                type="button"
                disabled={cartProducts.length === 0}
                onClick={applyCoupon}
                className="bg-gradient-to-r from-[#2a081a] to-[#4c0f2e] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:from-[#4c0f2e] hover:to-[#4c0f2e] transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
              >
                <i className="fas fa-check mr-2"></i>
                Apply Code
              </button>
            </div>

            {/* Applied message with enhanced styling */}
            {discountApplied && (
              <div className="flex items-center gap-3 mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-400 px-4 py-3 rounded-r-lg shadow-sm">
                <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                  <i className="fas fa-check-circle text-green-600 text-sm"></i>
                </div>
                <div className="flex-grow">
                  <p className="font-semibold text-green-800 text-sm">Success!</p>
                  <p className="text-green-700 text-xs">
                    <span className="font-mono bg-green-100 px-2 py-1 rounded">{couponCode}</span>{' '}
                    applied successfully
                  </p>
                </div>
                <button
                  onClick={() => {
                    setCouponCode('');
                    setDiscount(0);
                    setDiscountAmount(0);
                    setDiscountApplied(false);
                    toast.info('Coupon removed!');
                  }}
                  className="flex items-center justify-center w-8 h-8 text-green-600 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200"
                  title="Remove coupon"
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              </div>
            )}

            {/* Enhanced available coupon list */}
            {showCoupons && (
              <div className="mt-4 border-2 border-[#FFC3A1]/30 rounded-xl p-4 bg-white ">
                <div className="flex items-center gap-2 mb-3 border-b p-2 border-[#FFC3A1]/30">
                  <div className="flex items-center justify-center w-6 h-6 bg-[#ecdbdd] rounded-full">
                    <i className="fas fa-gifts text-[#2a081a] text-xs"></i>
                  </div>
                  <p className="font-bold text-[#4c0f2e] text-sm ">Available Coupons</p>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                  {availableCoupons.length > 0 ? (
                    availableCoupons.map((coupon) => (
                      <div
                        key={coupon.code}
                        onClick={() => {
                          setCouponCode(coupon.code);
                          setShowCoupons(false);
                        }}
                        className="group cursor-pointer hover:bg-white/70 p-3 rounded-lg border border-transparent hover:border-[#FFC3A1] hover:shadow-md transition-all duration-200 transform hover:scale-[1.02]"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-[#2a081a] rounded-md p-2 shadow-sm">
                            <Gift size={14} className="text-sm text-white" />
                          </div>
                          <div className="flex-grow">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-[#4c0f2e] text-sm group-hover:text-[#2a081a] transition-colors">
                                {coupon.code}
                              </span>
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 duration-200">
                                <ChevronRight className="w-3 h-3 text-[#2a081a]" />
                              </div>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{coupon.description}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-sm text-gray-600 py-4">
                      No active coupons available at the moment.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Order Summary */}
      <div className="w-full sm:w-1/2">
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 shadow-sm">
          <SectionTitle text1="ORDER" text2="SUMMARY" />

          {/* Product List */}
          <div className="mt-4 space-y-4 max-h-[300px] overflow-y-auto pr-2">
            {Object.entries(cartItems).map(([productId, sizes]) => {
              const product = cartProducts.find((p) => p._id === productId);
              if (!product) return null;

              return Object.entries(sizes).map(([size, quantity], idx) => {
                if (quantity <= 0) return null;

                return (
                  <div
                    key={productId + size + idx}
                    className="flex items-start gap-4 border-b pb-3"
                  >
                    <img
                      src={product.image[0]}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#4c0f2e]">{product.name}</p>
                      {product.sizes && product.sizes.length > 0 && (
                        <p className="text-xs text-gray-600">Size: {size}</p>
                      )}
                      <p className="text-xs text-gray-600">Qty: {quantity}</p>
                      <p className="text-sm text-[#A75D5D] font-semibold mt-1">
                        ₹ {(product.price * quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                );
              });
            })}
          </div>

          {/* Cost Summary */}
          {/* 🧾 Final Bill Summary */}
          <div className="mt-6 space-y-3 text-sm">
            <p className="font-medium text-gray-700 mb-2">PRICE DETAILS</p>

            {/* MRP Total */}
            <div className="flex justify-between items-center mb-1">
              <p className="text-gray-600">Total MRP</p>
              <p className="line-through text-gray-500">₹ {(subtotal * 1.3).toFixed(2)}</p>
            </div>

            {/* Product Discount */}
            <div className="flex justify-between items-center mb-1">
              <p className="text-gray-600">Product Discount</p>
              <p className="text-green-600">-₹ {(subtotal * 0.3).toFixed(2)}</p>
            </div>

            {/* Selling Price */}
            <div className="flex justify-between items-center mb-1">
              <p className="text-gray-600">Selling Price</p>
              <p>₹ {subtotal.toFixed(2)}</p>
            </div>

            {/* Coupon Discount */}
            <div className="flex justify-between items-center mb-1">
              <p className="text-gray-600">
                Coupon Discount{' '}
                {couponCode && <span className="text-[#4c0f2e] font-medium">({couponCode})</span>}
              </p>

              <p className={`font-medium ${discount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                -₹ {discountAmount.toFixed(2)}
              </p>
            </div>

            {/* Delivery Charges */}
            <div className="flex justify-between items-center mb-1 border-t border-gray-200 pt-2">
              <p className="text-gray-600">Shipping Charges</p>
              <p>
                {!canCalculateDelivery ? (
                  <span className="text-gray-500 italic">To be calculated</span>
                ) : adjustedSubtotal >= 500 ? (
                  <span className="text-green-600 font-medium">FREE</span>
                ) : (
                  `₹ ${deliveryFee.toFixed(2)}`
                )}
              </p>
            </div>

            {/* Free Delivery Progress */}
            {subtotal < 500 && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500"></span>
                  <span className="text-[#2a081a] font-medium">
                    {Math.min(100, ((subtotal / 500) * 100).toFixed(0))}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 border border-[#2a081a]/20">
                  <div
                    className="h-full rounded-full bg-[#2a081a] transition-all duration-500"
                    style={{ width: `${Math.min(100, (subtotal / 500) * 100)}%` }}
                  ></div>
                </div>
                <p className="text-[#2a081a] text-center text-xs mt-2">
                  Add <span className="font-bold">₹ {(500 - subtotal).toFixed(2)}</span> more to get{' '}
                  <span className="font-bold">FREE DELIVERY</span>
                </p>
              </div>
            )}

            {/* Grand Total */}
            <div className="flex justify-between items-center pt-3 border-t border-gray-200 font-bold text-base">
              <p>Total Amount</p>
              <p className="text-[#2a081a]">₹ {finalAmount.toFixed(2)}</p>
            </div>

            {/* Savings */}
            <div className="bg-green-50 p-2 rounded-md mt-3 text-center text-green-600 font-medium text-sm">
              You will save ₹ {(subtotal * 0.3 + discountAmount).toFixed(2)} on this order
            </div>
          </div>

          {/* Payment Method */}
          <div className="mt-6">
            <SectionTitle text1="PAYMENT" text2="METHOD" />
            <PaymentMethod />
          </div>

          {/* Place Order Button */}
          <div className="w-full text-center mt-8">
            <button
              type="submit"
              className="bg-[#2a081a] text-white rounded-md px-16 py-3 text-sm font-medium hover:bg-[#4c0f2e] transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'PLACING ORDER ...' : 'PLACE ORDER'}
            </button>
          </div>
        </div>
      </div>

      {/* Shipping Policy Modal */}
      {showShippingPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 sm:w-96 p-5 relative">
            {/* Close button */}
            <button
              className="absolute top-2 right-3 text-gray-500 text-xl"
              onClick={() => setShowShippingPolicy(false)}
            >
              &times;
            </button>

            {/* Shipping Policy Content */}
            <h2 className="text-lg font-semibold text-center font-poppins text-[#2a081a] mb-4">
              SHIPPING POLICY
            </h2>

            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-500">
              <li>
                <span className="font-medium text-gray-800">Shipping Charges:</span>
                Standard delivery fees vary by distance.
                <span className="font-medium text-[#4c0f2e]">Enjoy free shipping</span> on orders
                over ₹499! International shipping is based on destination and weight.
              </li>

              <li>
                <span className="font-medium text-gray-800">Processing & Delivery Time:</span>{' '}
                Domestic orders are dispatched within 2 working days and delivered in 3–5 working
                days. International orders are processed in 7 days and take 10–15 working days for
                delivery.
              </li>

              <li>
                <span className="font-medium text-gray-800">International Orders:</span> Currently
                accepted only via WhatsApp. Charges and delivery times depend on product weight and
                destination.
              </li>

              <li>
                <span className="font-medium text-gray-800">Order Tracking:</span> Tracking details
                are shared via email once shipped. Check your spam folder if you don’t see it. You
                can also{' '}
                <a
                  href="https://zarviya.com/trackyourOrder"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-800 underline"
                >
                  track your order here
                </a>
                .
              </li>
            </ul>
          </div>
        </div>
      )}
    </form>
  );
};

export default PlaceOrder;
